const HARD_REFRESH_STORAGE_KEY = 'gallixHardRefreshPending';
const HARD_REFRESH_QUERY_PARAM = '_hardRefresh';
const NGSW_BYPASS_QUERY_PARAM = 'ngsw-bypass';
const HARD_REFRESH_STAGE_PENDING_SECOND = 'pendingSecond';
const HARD_REFRESH_STAGE_COMPLETING = 'completing';

/**
 * Starts a cache-bypassing reload and schedules another hard refresh on the next page load.
 *
 * The first reload unregisters the service worker and clears Cache Storage so the browser
 * cannot keep serving the previous Angular build. A session flag then triggers a second
 * hard refresh after that navigation, which is required because an unregistered worker can
 * still control the current document until the following load.
 */
export async function startHardRefreshWithFollowUp(): Promise<void> {
  setHardRefreshStage(HARD_REFRESH_STAGE_PENDING_SECOND);
  await executeHardRefresh();
}

/**
 * Continues a follow-up hard refresh that was scheduled before the previous reload.
 *
 * Call this before bootstrapping Angular. When it returns `true`, skip bootstrap because
 * the page is about to navigate again.
 *
 * @returns {boolean} `true` when a second hard refresh has been started.
 */
export function handlePendingHardRefresh(): boolean {
  const stage: string | null = getHardRefreshStage();
  if (stage === HARD_REFRESH_STAGE_PENDING_SECOND) {
    setHardRefreshStage(HARD_REFRESH_STAGE_COMPLETING);
    void executeHardRefresh();
    return true;
  }

  if (stage === HARD_REFRESH_STAGE_COMPLETING) {
    clearHardRefreshStage();
    stripHardRefreshQueryParam();
  }

  return false;
}

/**
 * Unregisters service workers, deletes Cache Storage entries, and replaces the current URL
 * with cache-busting query parameters so the next navigation is not served from HTTP or ngsw cache.
 */
async function executeHardRefresh(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const registrations: readonly ServiceWorkerRegistration[] = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration: ServiceWorkerRegistration) => registration.unregister()));
    }
    if ('caches' in window) {
      const cacheKeys: readonly string[] = await caches.keys();
      await Promise.all(cacheKeys.map((cacheKey: string) => caches.delete(cacheKey)));
    }
  } catch {
    // Continue with navigation even if cache or service worker cleanup fails.
  }

  try {
    const url: URL = new URL(window.location.href);
    url.searchParams.set(HARD_REFRESH_QUERY_PARAM, Date.now().toString());
    url.searchParams.set(NGSW_BYPASS_QUERY_PARAM, 'true');
    window.location.replace(url.toString());
  } catch {
    window.location.reload();
  }
}

/**
 * Reads the hard-refresh stage from sessionStorage.
 *
 * @returns {string | null} The stored stage, or `null` when unavailable.
 */
function getHardRefreshStage(): string | null {
  try {
    return sessionStorage.getItem(HARD_REFRESH_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Writes the hard-refresh stage to sessionStorage.
 *
 * @param {string} stage - Stage to persist for the next page load.
 */
function setHardRefreshStage(stage: string): void {
  try {
    sessionStorage.setItem(HARD_REFRESH_STORAGE_KEY, stage);
  } catch {
    // Ignore storage failures (private mode, quota, etc.).
  }
}

/**
 * Removes the hard-refresh stage from sessionStorage.
 */
function clearHardRefreshStage(): void {
  try {
    sessionStorage.removeItem(HARD_REFRESH_STORAGE_KEY);
  } catch {
    // Ignore storage failures (private mode, quota, etc.).
  }
}

/**
 * Strips cache-busting query parameters from the URL without triggering another navigation.
 */
function stripHardRefreshQueryParam(): void {
  const url: URL = new URL(window.location.href);
  const hadHardRefreshParam: boolean = url.searchParams.has(HARD_REFRESH_QUERY_PARAM);
  const hadNgswBypassParam: boolean = url.searchParams.has(NGSW_BYPASS_QUERY_PARAM);
  if (!hadHardRefreshParam && !hadNgswBypassParam) {
    return;
  }

  url.searchParams.delete(HARD_REFRESH_QUERY_PARAM);
  url.searchParams.delete(NGSW_BYPASS_QUERY_PARAM);
  const nextUrl: string = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState(window.history.state, document.title, nextUrl);
}
